
import { collection, addDoc, doc, setDoc, updateDoc, deleteDoc, query, where, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { ref, uploadBytes, uploadBytesResumable, getDownloadURL, listAll, deleteObject } from 'firebase/storage';
import { firestore, storage } from './firebase';
import { DatabaseSoundPackSoundsModel } from './DatabaseSoundPackSounds';
import type { DatabaseUpcomingPack } from './DatabaseUpcomingPack';

// Which PackInfoPage layout variant a pack uses for its info page (see VOC-2223).
export type PackInfoStyle = 'style1' | 'style2';

export type DatabaseSoundPack = {
    id?: string; // Document ID from Firestore
    name: string;
    description?: string | null; // Description of the sound pack
    priceId?: string | null; // Corresponding Stripe price ID
    price?: string | null; // Price in USD without currency symbol (e.g., "9.99")
    public: boolean; // true = visible to all users, false = preview (admin only)
    factoryDefault?: boolean; // true = bundled into the plugin installer
    infoPageStyle?: PackInfoStyle; // info-page layout variant; defaults to 'style1'
    secondaryTitle?: string | null; // optional header subtitle, any style (e.g. "Vol 1")
    createdAt?: string;
    // Cached media URLs (stored during upload to avoid slow getDownloadURL calls)
    coverImageUrl?: string | null;
    videoPlaylistUrl?: string | null;
    videoThumbnailUrl?: string | null;
    fullWidthImageUrl?: string | null; // style2 full-width image (replaces video)
    demoZipUrl?: string | null;
    baseZipUrl?: string | null;
    demoTrackUrl?: string | null;
};

// Field name constants (kept private to this file)
class FieldPaths {
    static readonly NAME = "name";
    static readonly DESCRIPTION = "description";
    static readonly SECONDARY_TITLE = "secondaryTitle";
    static readonly PRICE_ID = "stripePriceId";
    static readonly PRICE = "price";
    static readonly PUBLIC = "public";
    static readonly FACTORY_DEFAULT = "factoryDefault";
    static readonly INFO_PAGE_STYLE = "infoPageStyle";
    static readonly CREATED_AT = "createdAt";
    static readonly COVER_IMAGE_URL = "coverImageUrl";
    static readonly VIDEO_PLAYLIST_URL = "videoPlaylistUrl";
    static readonly VIDEO_THUMBNAIL_URL = "videoThumbnailUrl";
    static readonly FULL_WIDTH_IMAGE_URL = "fullWidthImageUrl";
    static readonly DEMO_ZIP_URL = "demoZipUrl";
    static readonly BASE_ZIP_URL = "baseZipUrl";
    static readonly DEMO_TRACK_URL = "demoTrackUrl";
}

export class DatabaseSoundPackModel {
    static readonly COLLECTION_NAME = "sample_packs";

    private static get _soundPacksCollection() {
        return collection(firestore, this.COLLECTION_NAME);
    }

    /**
     * Formats a price for display in USD
     * @param price Price string without currency symbol (e.g., "9.99")
     * @returns Formatted price with currency symbol (e.g., "$9.99") or "N/A"
     */
    static formatPrice(price: string | null | undefined): string {
        if (!price) return 'N/A';

        const numPrice = parseFloat(price);
        if (isNaN(numPrice)) return 'N/A';

        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: Number.isInteger(numPrice) ? 0 : 2,
        }).format(numPrice);
    }

    /**
     * Comparator for sorting packs newest-first by createdAt. Packs missing a
     * createdAt keep their existing relative order. Shared by every consumer
     * (storefront, plugin browser, admin tabs) so pack ordering stays
     * consistent wherever a list is rendered.
     */
    static sortByCreatedAtDesc(a: DatabaseSoundPack, b: DatabaseSoundPack): number {
        if (!a.createdAt || !b.createdAt) return 0;
        return b.createdAt.localeCompare(a.createdAt);
    }

    static fromFirestore(data: any, docId?: string): DatabaseSoundPack {
        if (
            typeof data !== "object" ||
            !data ||
            typeof data[FieldPaths.NAME] !== "string" ||
            typeof data[FieldPaths.PUBLIC] !== "boolean"
        ) {
            throw new Error("Invalid sound pack document structure");
        }
        return {
            id: docId,
            name: data[FieldPaths.NAME],
            description: data[FieldPaths.DESCRIPTION] || null,
            secondaryTitle: data[FieldPaths.SECONDARY_TITLE] || null,
            priceId: data[FieldPaths.PRICE_ID] || null,
            price: data[FieldPaths.PRICE] || null,
            public: data[FieldPaths.PUBLIC],
            factoryDefault: data[FieldPaths.FACTORY_DEFAULT] === true,
            infoPageStyle: data[FieldPaths.INFO_PAGE_STYLE] === 'style2' ? 'style2' : 'style1',
            createdAt: data[FieldPaths.CREATED_AT] || null,
            coverImageUrl: data[FieldPaths.COVER_IMAGE_URL] || null,
            videoPlaylistUrl: data[FieldPaths.VIDEO_PLAYLIST_URL] || null,
            videoThumbnailUrl: data[FieldPaths.VIDEO_THUMBNAIL_URL] || null,
            fullWidthImageUrl: data[FieldPaths.FULL_WIDTH_IMAGE_URL] || null,
            demoZipUrl: data[FieldPaths.DEMO_ZIP_URL] || null,
            baseZipUrl: data[FieldPaths.BASE_ZIP_URL] || null,
            demoTrackUrl: data[FieldPaths.DEMO_TRACK_URL] || null,
        };
    }

    static toFirestore(pack: DatabaseSoundPack): any {
        const data: any = {
            [FieldPaths.NAME]: pack.name,
            [FieldPaths.PUBLIC]: pack.public,
            [FieldPaths.CREATED_AT]: pack.createdAt
        };

        if (pack.factoryDefault !== null && pack.factoryDefault !== undefined) {
            data[FieldPaths.FACTORY_DEFAULT] = pack.factoryDefault;
        }

        if (pack.infoPageStyle !== null && pack.infoPageStyle !== undefined) {
            data[FieldPaths.INFO_PAGE_STYLE] = pack.infoPageStyle;
        }

        if (pack.description !== null && pack.description !== undefined) {
            data[FieldPaths.DESCRIPTION] = pack.description;
        }

        if (pack.secondaryTitle !== null && pack.secondaryTitle !== undefined) {
            data[FieldPaths.SECONDARY_TITLE] = pack.secondaryTitle;
        }

        if (pack.priceId !== null && pack.priceId !== undefined) {
            data[FieldPaths.PRICE_ID] = pack.priceId;
        }

        if (pack.price !== null && pack.price !== undefined) {
            data[FieldPaths.PRICE] = pack.price;
        }

        if (pack.coverImageUrl !== null && pack.coverImageUrl !== undefined) {
            data[FieldPaths.COVER_IMAGE_URL] = pack.coverImageUrl;
        }

        if (pack.videoPlaylistUrl !== null && pack.videoPlaylistUrl !== undefined) {
            data[FieldPaths.VIDEO_PLAYLIST_URL] = pack.videoPlaylistUrl;
        }

        if (pack.videoThumbnailUrl !== null && pack.videoThumbnailUrl !== undefined) {
            data[FieldPaths.VIDEO_THUMBNAIL_URL] = pack.videoThumbnailUrl;
        }

        if (pack.fullWidthImageUrl !== null && pack.fullWidthImageUrl !== undefined) {
            data[FieldPaths.FULL_WIDTH_IMAGE_URL] = pack.fullWidthImageUrl;
        }

        if (pack.demoZipUrl !== null && pack.demoZipUrl !== undefined) {
            data[FieldPaths.DEMO_ZIP_URL] = pack.demoZipUrl;
        }

        if (pack.baseZipUrl !== null && pack.baseZipUrl !== undefined) {
            data[FieldPaths.BASE_ZIP_URL] = pack.baseZipUrl;
        }

        if (pack.demoTrackUrl !== null && pack.demoTrackUrl !== undefined) {
            data[FieldPaths.DEMO_TRACK_URL] = pack.demoTrackUrl;
        }

        return data;
    }

    // ====================== ADMIN API METHODS ============================

    /**
     * Create a new sound pack document
     * @param name The name of the sound pack
     * @param replacing Optional teaser this pack releases; the pack takes over
     * its document id and uses its release date as createdAt
     * @returns Promise<string> The document ID of the created pack
     */
    static async createSoundPack(name: string, replacing?: DatabaseUpcomingPack): Promise<string> {
        try {
            const packData: DatabaseSoundPack = {
                name,
                priceId: null,
                public: false,
                createdAt: replacing?.releaseDate ?? new Date().toISOString()
            };

            let packId: string;
            if (replacing?.id) {
                await setDoc(doc(firestore, this.COLLECTION_NAME, replacing.id), this.toFirestore(packData));
                packId = replacing.id;
            } else {
                packId = (await addDoc(this._soundPacksCollection, this.toFirestore(packData))).id;
            }

            console.log(`Successfully created sound pack "${name}", id = ${packId}`);
            return packId;
        } catch (error) {
            console.error('Error creating sound pack:', error);
            throw error;
        }
    }

    /**
     * Upload a file to storage for a sound pack
     * @param packId The document ID of the sound pack
     * @param file The file to upload
     * @param filename The filename (e.g., 'demo.zip', 'cover.png')
     * @param onProgress Optional callback for upload progress (0-100)
     * @returns Promise<string> The download URL
     */
    static async uploadFile(
        packId: string,
        file: File,
        filename: string,
        onProgress?: (percent: number) => void
    ): Promise<string> {
        try {
            const storageRef = ref(storage, `${this.COLLECTION_NAME}/${packId}/${filename}`);

            if (onProgress) {
                return new Promise((resolve, reject) => {
                    const uploadTask = uploadBytesResumable(storageRef, file);
                    uploadTask.on(
                        'state_changed',
                        (snapshot) => {
                            const percent = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
                            onProgress(percent);
                        },
                        (error) => {
                            console.error(`Error uploading ${filename}:`, error);
                            reject(error);
                        },
                        async () => {
                            const downloadURL = await getDownloadURL(storageRef);
                            resolve(downloadURL);
                        }
                    );
                });
            } else {
                await uploadBytes(storageRef, file);
                const downloadURL = await getDownloadURL(storageRef);
                return downloadURL;
            }
        } catch (error) {
            console.error(`Error uploading ${filename}:`, error);
            throw error;
        }
    }

    /**
     * Upload HLS video files (playlist + segments) to storage
     * @param packId The document ID of the sound pack
     * @param files Array of HLS files (.m3u8 playlist and .ts segments)
     * @param onProgress Optional callback for overall upload progress (0-100)
     * @returns Promise<string> The download URL of the .m3u8 playlist file
     */
    static async uploadHLSVideo(
        packId: string,
        files: File[],
        onProgress?: (percent: number) => void
    ): Promise<string> {
        try {
            const videoFolderPath = `${this.COLLECTION_NAME}/${packId}/video`;
            let playlistUrl = '';
            let playlistRef: ReturnType<typeof ref> | null = null;

            // Calculate total size for progress tracking
            const totalSize = files.reduce((acc, file) => acc + file.size, 0);
            let uploadedSize = 0;

            // Upload all files
            for (const file of files) {
                const storageRef = ref(storage, `${videoFolderPath}/${file.name}`);

                if (file.name.endsWith('.m3u8')) {
                    playlistRef = storageRef;
                }

                if (onProgress) {
                    await new Promise<void>((resolve, reject) => {
                        const uploadTask = uploadBytesResumable(storageRef, file);
                        uploadTask.on(
                            'state_changed',
                            (snapshot) => {
                                const currentFileProgress = snapshot.bytesTransferred;
                                const overallProgress = Math.round(((uploadedSize + currentFileProgress) / totalSize) * 100);
                                onProgress(overallProgress);
                            },
                            (error) => reject(error),
                            () => {
                                uploadedSize += file.size;
                                resolve();
                            }
                        );
                    });
                } else {
                    await uploadBytes(storageRef, file);
                }
            }

            if (!playlistRef) {
                throw new Error('No .m3u8 playlist file found in uploaded files');
            }

            playlistUrl = await getDownloadURL(playlistRef);

            console.log(`Successfully uploaded ${files.length} HLS files for pack ${packId}`);
            return playlistUrl;
        } catch (error) {
            console.error('Error uploading HLS video files:', error);
            throw error;
        }
    }



    /**
     * Update the Stripe price ID for a sound pack
     * @param packId The document ID of the sound pack
     * @param priceId The Stripe price ID
     */
    static async updatePriceId(packId: string, priceId: string): Promise<void> {
        try {
            await updateDoc(doc(firestore, this.COLLECTION_NAME, packId), {
                [FieldPaths.PRICE_ID]: priceId
            });
            console.log(`Updated price ID for pack ${packId}`);
        } catch (error) {
            console.error('Error updating price ID:', error);
            throw error;
        }
    }

    /**
     * Update the formatted price for a sound pack
     * @param packId The document ID of the sound pack
     * @param price The price in USD without currency symbol (e.g., "9.99")
     */
    static async updatePrice(packId: string, price: string): Promise<void> {
        try {
            await updateDoc(doc(firestore, this.COLLECTION_NAME, packId), {
                [FieldPaths.PRICE]: price
            });
            console.log(`Updated price for pack ${packId}`);
        } catch (error) {
            console.error('Error updating price:', error);
            throw error;
        }
    }

    /**
     * Update the name of a sound pack
     * @param packId The document ID of the sound pack
     * @param name The new name
     */
    static async updateName(packId: string, name: string): Promise<void> {
        try {
            await updateDoc(doc(firestore, this.COLLECTION_NAME, packId), {
                [FieldPaths.NAME]: name
            });
            console.log(`Updated name for pack ${packId}`);
        } catch (error) {
            console.error('Error updating name:', error);
            throw error;
        }
    }

    /**
     * Update the createdAt timestamp of a sound pack. Packs are listed sorted
     * by createdAt, so backdating/postdating this is how an admin reorders a
     * pack's position without a dedicated ordering field.
     * @param packId The document ID of the sound pack
     * @param createdAt ISO 8601 timestamp string
     */
    static async updateCreatedAt(packId: string, createdAt: string): Promise<void> {
        try {
            await updateDoc(doc(firestore, this.COLLECTION_NAME, packId), {
                [FieldPaths.CREATED_AT]: createdAt
            });
            console.log(`Updated createdAt for pack ${packId}`);
        } catch (error) {
            console.error('Error updating createdAt:', error);
            throw error;
        }
    }

    /**
     * Update the description of a sound pack
     * @param packId The document ID of the sound pack
     * @param description The new description
     */
    static async updateDescription(packId: string, description: string): Promise<void> {
        try {
            await updateDoc(doc(firestore, this.COLLECTION_NAME, packId), {
                [FieldPaths.DESCRIPTION]: description
            });
            console.log(`Updated description for pack ${packId}`);
        } catch (error) {
            console.error('Error updating description:', error);
            throw error;
        }
    }

    /**
     * Update the secondary title (optional header subtitle) of a sound pack
     * @param packId The document ID of the sound pack
     * @param secondaryTitle The new secondary title
     */
    static async updateSecondaryTitle(packId: string, secondaryTitle: string): Promise<void> {
        try {
            await updateDoc(doc(firestore, this.COLLECTION_NAME, packId), {
                [FieldPaths.SECONDARY_TITLE]: secondaryTitle
            });
            console.log(`Updated secondary title for pack ${packId}`);
        } catch (error) {
            console.error('Error updating secondary title:', error);
            throw error;
        }
    }

    /**
     * Set the public status of a sound pack
     * @param packId The document ID of the sound pack
     * @param isPublic true to make public, false to make private (preview)
     */
    static async setPublicStatus(packId: string, isPublic: boolean): Promise<void> {
        try {
            await updateDoc(doc(firestore, this.COLLECTION_NAME, packId), {
                [FieldPaths.PUBLIC]: isPublic
            });
            console.log(`Set sound pack ${packId} public status to ${isPublic}`);
        } catch (error) {
            console.error('Error setting public status:', error);
            throw error;
        }
    }

    /**
     * Mark a sound pack as a factory default (bundled into the plugin installer)
     * or not. Independent of `public`: a private factory pack ships in the
     * installer but stays hidden in the plugin until it is published.
     * @param packId The document ID of the sound pack
     * @param isFactory true to bundle into the installer, false to exclude
     */
    static async setFactoryDefault(packId: string, isFactory: boolean): Promise<void> {
        try {
            await updateDoc(doc(firestore, this.COLLECTION_NAME, packId), {
                [FieldPaths.FACTORY_DEFAULT]: isFactory
            });
            console.log(`Set sound pack ${packId} factoryDefault to ${isFactory}`);
        } catch (error) {
            console.error('Error setting factoryDefault:', error);
            throw error;
        }
    }

    /**
     * Set the info-page layout variant for a sound pack. Read back by the
     * plugin's PackInfoPage to pick which layout to render.
     * @param packId The document ID of the sound pack
     * @param style The layout variant ('style1' or 'style2')
     */
    static async setInfoPageStyle(packId: string, style: PackInfoStyle): Promise<void> {
        try {
            await updateDoc(doc(firestore, this.COLLECTION_NAME, packId), {
                [FieldPaths.INFO_PAGE_STYLE]: style
            });
            console.log(`Set info page style for pack ${packId} to ${style}`);
        } catch (error) {
            console.error('Error setting info page style:', error);
            throw error;
        }
    }

    /**
     * Update cached media URLs for a sound pack
     * @param packId The document ID of the sound pack
     * @param urls Object containing the URLs to cache
     */
    static async updateMediaUrls(packId: string, urls: {
        coverImageUrl?: string;
        videoPlaylistUrl?: string;
        videoThumbnailUrl?: string;
        fullWidthImageUrl?: string;
        demoZipUrl?: string;
        baseZipUrl?: string;
        demoTrackUrl?: string;
    }): Promise<void> {
        try {
            const updateData: Record<string, string> = {};
            if (urls.coverImageUrl) {
                updateData[FieldPaths.COVER_IMAGE_URL] = urls.coverImageUrl;
            }
            if (urls.videoPlaylistUrl) {
                updateData[FieldPaths.VIDEO_PLAYLIST_URL] = urls.videoPlaylistUrl;
            }
            if (urls.videoThumbnailUrl) {
                updateData[FieldPaths.VIDEO_THUMBNAIL_URL] = urls.videoThumbnailUrl;
            }
            if (urls.fullWidthImageUrl) {
                updateData[FieldPaths.FULL_WIDTH_IMAGE_URL] = urls.fullWidthImageUrl;
            }
            if (urls.demoZipUrl) {
                updateData[FieldPaths.DEMO_ZIP_URL] = urls.demoZipUrl;
            }
            if (urls.baseZipUrl) {
                updateData[FieldPaths.BASE_ZIP_URL] = urls.baseZipUrl;
            }
            if (urls.demoTrackUrl) {
                updateData[FieldPaths.DEMO_TRACK_URL] = urls.demoTrackUrl;
            }

            if (Object.keys(updateData).length > 0) {
                await updateDoc(doc(firestore, this.COLLECTION_NAME, packId), updateData);
                console.log(`Updated media URLs for pack ${packId}`);
            }
        } catch (error) {
            console.error('Error updating media URLs:', error);
            throw error;
        }
    }


    /**
     * Delete a sound pack document and all its storage files
     * @param packId The document ID of the sound pack
     */
    static async deleteSoundPack(packId: string): Promise<void> {
        try {
            // Delete all files in storage
            const folderRef = ref(storage, `${this.COLLECTION_NAME}/${packId}`);
            const fileList = await listAll(folderRef);

            // Delete all files in root folder
            const deletePromises = fileList.items.map(item => deleteObject(item));

            // Delete all files in subfolders (like video/)
            for (const folder of fileList.prefixes) {
                const subFiles = await listAll(folder);
                deletePromises.push(...subFiles.items.map(item => deleteObject(item)));
            }

            await Promise.all(deletePromises);

            // Delete the Firestore document
            await deleteDoc(doc(firestore, this.COLLECTION_NAME, packId));

            // Delete the pack's published sound list (sound_packs_sounds/{packId})
            await DatabaseSoundPackSoundsModel.deletePackSounds(packId);

            console.log(`Deleted sound pack ${packId} and all its files`);
        } catch (error) {
            console.error('Error deleting sound pack:', error);
            throw error;
        }
    }


    // ====================== ADMIN API METHODS ============================

    /**
     * Subscribe to sound packs. With includePreview=true, returns all packs
     * (public + preview); admin Firestore rules required. With false, only
     * public packs. Fires immediately with the SDK's cached snapshot (instant
     * on subsequent sessions) then revalidates against the server. Returns an
     * Unsubscribe — call it on cleanup.
     */
    static subscribeSoundPacks(
        includePreview: boolean,
        onUpdate: (packs: DatabaseSoundPack[]) => void,
        onError?: (error: Error) => void,
    ): Unsubscribe {
        const q = includePreview
            ? query(this._soundPacksCollection)
            : query(this._soundPacksCollection, where(FieldPaths.PUBLIC, '==', true));
        return onSnapshot(
            q,
            (snapshot) => {
                const packs: DatabaseSoundPack[] = [];
                snapshot.forEach((doc) => {
                    try {
                        packs.push(this.fromFirestore(doc.data(), doc.id));
                    } catch (error) {
                        console.error(`Error parsing pack ${doc.id}:`, error);
                    }
                });
                onUpdate(packs);
            },
            (error) => {
                console.error('Error in sound packs subscription:', error);
                onError?.(error);
            },
        );
    }

    /// ====================== MEDIA URL METHODS ============================
    // These URLs are cached in Firestore during upload to avoid slow getDownloadURL calls
    // but these methods are provided if direct URL fetching is needed.

    /**
     * Get the demo zip download URL for a sound pack
     * @param packId The document ID of the sound pack
     * @returns Promise<string> The download URL for the demo.zip
     */
    static async getDemoZipUrl(packId: string): Promise<string> {
        const demoRef = ref(storage, `${this.COLLECTION_NAME}/${packId}/demo.zip`);
        return await getDownloadURL(demoRef);
    }

    /**
     * Get the base (full) zip download URL for a sound pack
     * @param packId The document ID of the sound pack
     * @returns Promise<string> The download URL for the base.zip
     */
    static async getBaseZipUrl(packId: string): Promise<string> {
        const baseRef = ref(storage, `${this.COLLECTION_NAME}/${packId}/base.zip`);
        return await getDownloadURL(baseRef);
    }

    /**
     * Get the HLS video playlist URL for a sound pack
     * @param packId The document ID of the sound pack
     * @returns Promise<string> The download URL for the .m3u8 playlist
     */
    static async getVideoPlaylistUrl(packId: string): Promise<string> {

        try {
            const videoFolderRef = ref(storage, `${this.COLLECTION_NAME}/${packId}/video`);
            const fileList = await listAll(videoFolderRef);

            // Find the .m3u8 file
            const playlistFile = fileList.items.find(item => item.name.endsWith('.m3u8'));
            if (!playlistFile) {
                throw new Error(`No .m3u8 playlist found for pack ${packId}`);
            }

            return await getDownloadURL(playlistFile);
        } catch (error) {
            console.error('Error getting video playlist URL:', error);
            throw error;
        }
    }

    /**
     * Get the cover image download URL for a sound pack
     * @param packId The document ID of the sound pack
     * @returns Promise<string> The download URL for the cover.png
     */
    static async getCoverImageUrl(packId: string): Promise<string> {
        const coverRef = ref(storage, `${this.COLLECTION_NAME}/${packId}/cover.png`);
        return await getDownloadURL(coverRef);
    }

    /**
     * Get the video thumbnail URL for a sound pack
     * @param packId The document ID of the sound pack
     * @returns Promise<string> The download URL for the video thumbnail
     */
    static async getVideoThumbnailUrl(packId: string): Promise<string> {
        const thumbnailRef = ref(storage, `${this.COLLECTION_NAME}/${packId}/video/thumbnail.jpg`);
        return await getDownloadURL(thumbnailRef);
    }

    /**
     * Get the folder path for a sound pack in Cloud Storage
     * @param packId The document ID of the sound pack
     * @returns string The Cloud Storage folder path
     */
    static getPackFolderPath(packId: string): string {
        return `${this.COLLECTION_NAME}/${packId}`;
    }
}